import * as React from "react";

import { APPLICABLE_STATE_NAME, STARRED_ACTION_TYPE } from "utils/DexieDBManager/dexieDB.models";

type DocumentStatus = {
  isUnread?: boolean;
  isAckRequired?: boolean;
  isInitialized?: boolean;
  isStarredDocument?: boolean;
  documentVersionType?: APPLICABLE_STATE_NAME;
};

type IsAckRequiredDocumentsCount = {
  isAcknowledgementPageInitialized?: boolean;
  isCategoriesPageInitialized?: boolean;
  isStarredPageInitialized?: boolean;
  synchronizationId: string;
  documentsVersionCount: number;
  applicablesDocumentsCount: number;
  nextApplicablesDocumentsCount: number;
  isAckRequiredApplicableDocumentsCount: number;
  isAckRequiredNextApplicableDocumentsCount: number;
};
type SynchronizationItemToUpdate = {
  synchronizationId: string;
  documentVersionType: APPLICABLE_STATE_NAME;
};

type InitialDocumentStatus = DocumentStatus & { parentDocumentId: string };
export type InitialIsAckRequiredDocumentsState = IsAckRequiredDocumentsCount & {
  synchronizationId: string;
};
type Action =
  | { type: "init"; initialDocumentStatus: InitialDocumentStatus }
  | {
      type: "updateStarredIsAckRequiredDocumentsState";
      synchronizationItemToUpdate: SynchronizationItemToUpdate & {
        starredActionType?: STARRED_ACTION_TYPE;
        isAckRequired: boolean
      };
    }
  | { type: "toggleIsAckRequired "; parentDocumentId: string }
  | {
      type: "initCategoriesIsAckRequiredDocumentsState";
      isAckRequiredDocumentsState: InitialIsAckRequiredDocumentsState;
    }
  | {
      type: "initStarredIsAckRequiredDocumentsState";
      isAckRequiredDocumentsState: InitialIsAckRequiredDocumentsState;
    }
  | {
      type: "initAcknowledgementIsAckRequiredDocumentsState";
      isAckRequiredDocumentsState: InitialIsAckRequiredDocumentsState;
    }
  | { type: "toggleIsRead"; parentDocumentId: string }
  | { type: "toggleIsFavorite"; parentDocumentId: string };

type Dispatch = (action: Action) => void;

export type DocumentActionsStatusState = {
  documentActionsStatus: Map<string, DocumentStatus>;
  starredIsAckRequiredDocumentsState: Map<string, IsAckRequiredDocumentsCount>;
  categoriesIsAckRequiredDocumentsState: Map<string, IsAckRequiredDocumentsCount>;
  acknowledgementIsAckRequiredDocumentsState: Map<string, IsAckRequiredDocumentsCount>;
};

type DocumentActionsStatusProviderProps = { children: React.ReactNode };

export const DocumentActionsStatusContext = React.createContext<
  { state: DocumentActionsStatusState; dispatch: Dispatch } | undefined
>(undefined);

function documentActionsStatusReducer(
  state: DocumentActionsStatusState,
  action: Action
) {
  switch (action.type) {
    case "init": {
      state.documentActionsStatus.set(
        action.initialDocumentStatus.parentDocumentId,
        {
          documentVersionType: action.initialDocumentStatus.documentVersionType,
          isAckRequired: Boolean(action.initialDocumentStatus.isAckRequired),
          isStarredDocument: Boolean(action.initialDocumentStatus.isStarredDocument),
          isUnread: Boolean(action.initialDocumentStatus.isUnread),
          isInitialized: true,
        }
      );
      return { ...state };
    }
    case "initCategoriesIsAckRequiredDocumentsState": {
      const {
        synchronizationId,
        documentsVersionCount,
        applicablesDocumentsCount,
        isCategoriesPageInitialized,
        nextApplicablesDocumentsCount,
        isAckRequiredApplicableDocumentsCount,
        isAckRequiredNextApplicableDocumentsCount
      } = action.isAckRequiredDocumentsState;
      
      state.categoriesIsAckRequiredDocumentsState.set(
        action.isAckRequiredDocumentsState.synchronizationId,
        {
          synchronizationId,
          documentsVersionCount,
          applicablesDocumentsCount,
          isCategoriesPageInitialized ,
          nextApplicablesDocumentsCount,
          isAckRequiredApplicableDocumentsCount,
          isAckRequiredNextApplicableDocumentsCount,
        }
      );
      return { ...state };
    }
    case "initStarredIsAckRequiredDocumentsState": {
      const {
        synchronizationId,
        documentsVersionCount,
        isStarredPageInitialized,
        applicablesDocumentsCount,
        nextApplicablesDocumentsCount,
        isAckRequiredApplicableDocumentsCount,
        isAckRequiredNextApplicableDocumentsCount
      } = action.isAckRequiredDocumentsState;

      state.starredIsAckRequiredDocumentsState.set(
        action.isAckRequiredDocumentsState.synchronizationId,
        {
          synchronizationId,
          documentsVersionCount,
          isStarredPageInitialized,
          applicablesDocumentsCount,
          nextApplicablesDocumentsCount,
          isAckRequiredApplicableDocumentsCount,
          isAckRequiredNextApplicableDocumentsCount,
        }
      );
      return { ...state };
    }
    case "initAcknowledgementIsAckRequiredDocumentsState": {
      const {
        synchronizationId,
        documentsVersionCount,
        applicablesDocumentsCount,
        nextApplicablesDocumentsCount,
        isAcknowledgementPageInitialized,
        isAckRequiredApplicableDocumentsCount,
        isAckRequiredNextApplicableDocumentsCount
      } = action.isAckRequiredDocumentsState;

      state.acknowledgementIsAckRequiredDocumentsState.set(
        action.isAckRequiredDocumentsState.synchronizationId,
        {
          synchronizationId,
          documentsVersionCount,
          applicablesDocumentsCount,
          nextApplicablesDocumentsCount,
          isAcknowledgementPageInitialized,
          isAckRequiredApplicableDocumentsCount,
          isAckRequiredNextApplicableDocumentsCount,
        }
      );
      return { ...state };
    }
    case "updateStarredIsAckRequiredDocumentsState": {
      const synchronizationItemToUpdate = action.synchronizationItemToUpdate;
      const foundAckRequiredFavoriteDocuments = state.starredIsAckRequiredDocumentsState.get(synchronizationItemToUpdate.synchronizationId) as IsAckRequiredDocumentsCount;

      let isAckRequiredApplicableDocumentsCount = foundAckRequiredFavoriteDocuments?.isAckRequiredApplicableDocumentsCount ?? 0;
      let isAckRequiredNextApplicableDocumentsCount = foundAckRequiredFavoriteDocuments?.isAckRequiredNextApplicableDocumentsCount ?? 0;
      let applicablesDocumentsCount = foundAckRequiredFavoriteDocuments?.applicablesDocumentsCount ?? 0;
      let nextApplicablesDocumentsCount = foundAckRequiredFavoriteDocuments?.nextApplicablesDocumentsCount ?? 0;
      
      if (action.synchronizationItemToUpdate.documentVersionType === APPLICABLE_STATE_NAME.APPLICABLE) {
        if(synchronizationItemToUpdate.starredActionType === STARRED_ACTION_TYPE.ADD && synchronizationItemToUpdate.isAckRequired) {
          isAckRequiredApplicableDocumentsCount = isAckRequiredApplicableDocumentsCount + 1
          applicablesDocumentsCount = applicablesDocumentsCount + 1;
        } else if(synchronizationItemToUpdate.starredActionType === STARRED_ACTION_TYPE.ADD && !synchronizationItemToUpdate.isAckRequired) {
          applicablesDocumentsCount = applicablesDocumentsCount + 1;
        } else if(synchronizationItemToUpdate.starredActionType === STARRED_ACTION_TYPE.DELETE && synchronizationItemToUpdate.isAckRequired) {
          isAckRequiredApplicableDocumentsCount = isAckRequiredApplicableDocumentsCount === 0 ? 0 : isAckRequiredApplicableDocumentsCount - 1;
          applicablesDocumentsCount = applicablesDocumentsCount === 0 ? 0 : applicablesDocumentsCount - 1;
        } else {
          applicablesDocumentsCount = applicablesDocumentsCount === 0 ? 0 : applicablesDocumentsCount - 1;
        }
      } else {
        if(synchronizationItemToUpdate.starredActionType === STARRED_ACTION_TYPE.ADD && synchronizationItemToUpdate.isAckRequired) {
        isAckRequiredNextApplicableDocumentsCount = isAckRequiredNextApplicableDocumentsCount + 1
        nextApplicablesDocumentsCount = nextApplicablesDocumentsCount + 1;
      } else if(synchronizationItemToUpdate.starredActionType === STARRED_ACTION_TYPE.ADD && !synchronizationItemToUpdate.isAckRequired) {
        nextApplicablesDocumentsCount = nextApplicablesDocumentsCount + 1;
      } else if(synchronizationItemToUpdate.starredActionType === STARRED_ACTION_TYPE.DELETE && synchronizationItemToUpdate.isAckRequired) {
        isAckRequiredNextApplicableDocumentsCount = isAckRequiredNextApplicableDocumentsCount === 0 ? 0 : isAckRequiredNextApplicableDocumentsCount - 1;
        nextApplicablesDocumentsCount = nextApplicablesDocumentsCount === 0 ? 0 : nextApplicablesDocumentsCount - 1;
      } else {
        nextApplicablesDocumentsCount = nextApplicablesDocumentsCount === 0 ? 0 : nextApplicablesDocumentsCount - 1;
      }
    }

      state.starredIsAckRequiredDocumentsState.set(
        synchronizationItemToUpdate.synchronizationId,
        {
          ...foundAckRequiredFavoriteDocuments,
          applicablesDocumentsCount,
          nextApplicablesDocumentsCount,
          isAckRequiredApplicableDocumentsCount,
          isAckRequiredNextApplicableDocumentsCount,
          documentsVersionCount: applicablesDocumentsCount + nextApplicablesDocumentsCount ,
        }
      );

      return { ...state };
    }
    case "toggleIsAckRequired ": {
      state.documentActionsStatus.set(action.parentDocumentId, {
        ...state.documentActionsStatus.get(action.parentDocumentId),
        isAckRequired: false,
      });
      return { ...state };
    }
    case "toggleIsRead": {
      state.documentActionsStatus.set(action.parentDocumentId, {
        ...state.documentActionsStatus.get(action.parentDocumentId),
        isUnread: false,
      });
      return { ...state };
    }
    case "toggleIsFavorite": {
      state.documentActionsStatus.set(action.parentDocumentId, {
        ...state.documentActionsStatus.get(action.parentDocumentId),
        isStarredDocument: !state.documentActionsStatus.get(action.parentDocumentId)?.isStarredDocument,
      });
      return { ...state };
    }
    default: {
      throw new Error(`Unhandled action type`);
    }
  }
}

function DocumentActionsStatusProvider({
  children,
}: DocumentActionsStatusProviderProps) {
  const [state, dispatch] = React.useReducer(documentActionsStatusReducer, {
    categoriesIsAckRequiredDocumentsState: new Map<string, IsAckRequiredDocumentsCount>(),
    starredIsAckRequiredDocumentsState: new Map<string, IsAckRequiredDocumentsCount>(),
    acknowledgementIsAckRequiredDocumentsState: new Map<string, IsAckRequiredDocumentsCount>(),
    documentActionsStatus: new Map<string, DocumentStatus>(), 
  });

  const memoDispatch = React.useMemo(() => dispatch, []);
  const value = { state, dispatch: memoDispatch };
  return (
    <DocumentActionsStatusContext.Provider value={value}>
      {children}
    </DocumentActionsStatusContext.Provider>
  );
}

function useDocumentActionsStatus() {
  const context = React.useContext(DocumentActionsStatusContext);
  if (context === undefined) {
    throw new Error("useDocumentActionsStatus must be used within a DocumentActionsStatusProvider");
  }
  return context;
}

export { DocumentActionsStatusProvider, useDocumentActionsStatus };
